import * as React from 'react';

import { toCountFormat, toExactRupiahFormat, toJutaFormat, toPercentageFormat } from './Dashboard.formatters';
import { DashboardBusinessHealth } from './Dashboard.queries';
import { StatCard } from './StatCard';
import { TimelinessCard } from './TimelinessCard';

interface BusinessHealthSectionProps {
  businessHealth: DashboardBusinessHealth;
  comparisonLabel: string;
}

export const BusinessHealthSection = (props: BusinessHealthSectionProps) => {
  const { businessHealth, comparisonLabel } = props;
  const { volumeBreakdown, totalVolume } = businessHealth;
  const volumeValue = totalVolume.value;
  const gallonShare = volumeValue > 0 ? (volumeBreakdown.gallon / volumeValue) * 100 : 0;
  const smallpackShare = volumeValue > 0 ? (volumeBreakdown.smallpack / volumeValue) * 100 : 0;

  // Below a million the abbreviation IS the exact figure, so showing both would just repeat itself.
  const revenueHeadline = toJutaFormat(businessHealth.revenue.value);
  const revenueExact = toExactRupiahFormat(businessHealth.revenue.value);

  return (
    <div className="Dashboard__grid Dashboard__grid--health">
      <StatCard
        accent="blue"
        comparisonLabel={comparisonLabel}
        deltaPercentage={businessHealth.totalOrders.deltaPercentage}
        icon="🛒"
        label="Total Orders"
        unit="orders"
        value={toCountFormat(businessHealth.totalOrders.value)}
      />

      <StatCard
        accent="green"
        comparisonLabel={comparisonLabel}
        deltaPercentage={totalVolume.deltaPercentage}
        icon="📦"
        label="Total Volume Sold"
        unit="pcs (galon + smallpack)"
        value={toCountFormat(volumeValue)}
      >
        <div className="Dashboard__breakdown">
          <div className="Dashboard__breakdown-row">
            <span className="Dashboard__dot Dashboard__dot--blue" />
            <span className="Dashboard__breakdown-label">Galon</span>
            <span className="Dashboard__breakdown-value">
              {toCountFormat(volumeBreakdown.gallon)} pcs ({toPercentageFormat(gallonShare)})
            </span>
          </div>
          <div className="Dashboard__breakdown-row">
            <span className="Dashboard__dot Dashboard__dot--green" />
            <span className="Dashboard__breakdown-label">Smallpack</span>
            <span className="Dashboard__breakdown-value">
              {toCountFormat(volumeBreakdown.smallpack)} pcs ({toPercentageFormat(smallpackShare)})
            </span>
          </div>
        </div>
      </StatCard>

      <StatCard
        accent="purple"
        comparisonLabel={comparisonLabel}
        deltaPercentage={businessHealth.revenue.deltaPercentage}
        icon="💰"
        exactValue={revenueExact === revenueHeadline ? undefined : revenueExact}
        label="Revenue"
        value={revenueHeadline}
      />

      <TimelinessCard
        buckets={businessHealth.timeliness}
        uncalculatedOrders={businessHealth.timelinessUncalculatedOrders}
      />

      <StatCard
        accent="teal"
        comparisonLabel={comparisonLabel}
        deltaPercentage={businessHealth.activeCustomers.deltaPercentage}
        icon="👥"
        label="Active Customers"
        unit="customers"
        value={toCountFormat(businessHealth.activeCustomers.value)}
      />

      <StatCard
        accent="indigo"
        comparisonLabel={comparisonLabel}
        deltaPercentage={businessHealth.returningCustomerRate.deltaPercentage}
        icon="🔄"
        label="Returning Customer Rate"
        unit="of previous period's customers who ordered again"
        value={toPercentageFormat(businessHealth.returningCustomerRate.value)}
      />
    </div>
  );
};
